import { useRef } from "react";
import { Card } from "../../../components/ui/card";
import { centsToCurrency } from "../../utils/money";

interface AppointmentsChartProps {
  lang: "pt" | "en";
  t: any;
  loading: boolean;
  data: any[];
  yTicks: number[];
  yMax: number;
  employees: any[];
  hover: any;
  setHover: (hover: any) => void;
}

export function AppointmentsChart({
  lang,
  t,
  loading,
  data,
  yTicks,
  yMax,
  employees,
  hover,
  setHover,
}: AppointmentsChartProps) {
  const barsRef = useRef<HTMLDivElement | null>(null);

  return (
    <Card>
      <div className="p-4 overflow-hidden">
        {loading ? (
          <div>{t.common.loading}</div>
        ) : data.length === 0 ? (
          <div>{t.common.none}</div>
        ) : (
          <div className="h-64">
            <div className="relative h-full">
              {/* Y-axis ticks */}
              <div className="absolute left-0 top-0 bottom-6 w-10 text-[10px] text-neutral-500">
                <div className="relative h-full">
                  {yTicks.map((value) => (
                    <div
                      key={value}
                      className="absolute left-0 translate-y-1/2"
                      style={{ bottom: `${(value / yMax) * 100}%` }}
                    >
                      <span className="block w-8 text-right">{value}</span>
                    </div>
                  ))}
                </div>
              </div>
              {/* Grid + bars */}
              <div className="absolute left-10 right-2 top-0 bottom-6">
                <div className="absolute inset-0 z-0">
                  {yTicks
                    .filter((v, i) => i !== 0 && i !== yTicks.length - 1)
                    .map((v) => (
                      <div
                        key={v}
                        className="absolute left-0 right-0 border-t border-neutral-200"
                        style={{ bottom: `${(v / yMax) * 100}%` }}
                      />
                    ))}
                </div>
                <div
                  ref={barsRef}
                  className="h-full flex items-end gap-px relative z-10"
                  onMouseLeave={() => setHover(null)}
                >
                  {data.map((d: any, idxBar: number) => {
                    const totalCount = d.total ?? d.count ?? 0;
                    return (
                      <div key={d.day} className="flex-1 min-w-[3px] h-full flex flex-col justify-end items-center">
                        <div className="w-full flex flex-col justify-end h-full">
                          {(d.parts && d.parts.length ? d.parts : [{ id: -1, count: totalCount }]).map((p: any) => {
                            const emp = employees.find((e) => e.id === p.id);
                            const color = emp?.color || "var(--chart-1, #6d28d9)";
                            const hp = (p.count / yMax) * 100;
                            const h = `calc(${hp}%${p.count > 0 ? " + 1px" : ""})`;
                            return (
                              <div
                                key={p.id}
                                className="w-full"
                                style={{ height: h, background: color, borderRadius: 4 }}
                                onMouseEnter={(e) => {
                                  const rect = barsRef.current?.getBoundingClientRect();
                                  const x = rect ? e.clientX - rect.left : 0;
                                  const y = rect ? e.clientY - rect.top : 0;
                                  setHover({
                                    idx: idxBar,
                                    name: emp?.name?.split(" ")[0] || t.dashboard.tooltipName,
                                    count: p.count,
                                    revenue: p.revenue,
                                    x,
                                    y,
                                  });
                                }}
                                onMouseMove={(e) => {
                                  const rect = barsRef.current?.getBoundingClientRect();
                                  if (!rect) return;
                                  const x = e.clientX - rect.left;
                                  const y = e.clientY - rect.top;
                                  setHover((h: any) => (h ? { ...h, x, y } : h));
                                }}
                              />
                            );
                          })}
                        </div>
                      </div>
                    );
                  })}
                  {hover && (
                    <div
                      className="absolute text-[11px] px-2 py-1 rounded bg-neutral-900 text-white shadow pointer-events-none z-20 whitespace-nowrap"
                      style={{ left: `${hover.x}px`, top: `${hover.y}px`, transform: "translate(-50%, -110%)" }}
                    >
                      {hover.name}: {hover.count}{" "}
                      {hover.revenue > 0 ? `(${centsToCurrency(hover.revenue, lang)})` : ""}
                    </div>
                  )}
                </div>
              </div>
              {/* X-axis labels */}
              <div className="absolute left-10 right-2 bottom-0 h-6 text-[10px] text-neutral-500 flex">
                {data.map((d, i) => (
                  <div key={d.day} className="flex-1 text-center truncate">
                    {(() => {
                      const label = d.day.slice(5); // MM-DD
                      const every = Math.ceil(data.length / 12);
                      return i % every === 0 ? label : "\u00A0";
                    })()}
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>
    </Card>
  );
}
